import React, { useEffect, useState } from "react";
import PdcLayout from "./_PdcLayout";
import { makeStyles } from "@material-ui/core/styles";
import { PdcSelect } from "../common/PdcSelect";
import { Button } from "@material-ui/core";
import { useSelector } from "react-redux";
import useCloneDetails from "../hooks/useCloneDetails";
import { useCallback } from "react";
import { useMemo } from "react";

const useStyles = makeStyles((theme) => ({
  inputGroup: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "40px",
  },
  label: {
    color: "#515151",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal",
  },
  text_field: {
    background: "#fff",
    width: "100%",
    maxWidth: "380px",
    height: "40px",
    maxHeight: "40px",
  },
  btn: {
    backgroundColor: "#F0B429",
    borderRadius: "3px",
    textTransform: "capitalize",
    padding: "12px 32px",
    fontStyle: "normal",
    fontSize: "16px",
    fontWeight: "600",
    lineHeight: "normal",
  },
  btnCancel: {
    color: "#626262",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal",
    padding: "0px",
    background: "transparent",
    border: "none",
  },
  btnGroup: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "78px",
    gap: "32px",
  },
}));

const AppToWorkModal = ({ setData, open, handleClose, goToNext }) => {
  const classes = useStyles();
  const [selectedSourceOrgValue, setSelectedSourceOrgValue] = useState({});
  const [selectedDestOrgValue, setSelectedDestOrgValue] = useState({});
  const [selectedSourceSpaceValue, setSelectedSourceSpaceValue] = useState({});
  const [selectedDestSpaceValue, setSelectedDestSpaceValue] = useState({});
  const [orgInfo, setOrgInfo] = useState("");

  const { sourceOrganisations, sourceSpaces, destOrganisations, destSpaces } =
    useSelector((state) => state.clone);

  const {
    sourceOrgsData,
    sourceSpacesData,
    destOrgsData,
    destSpacesData,
    selectedSourceOrg,
    selectedSourceSpace,
    selectedDestOrg,
    selectedDestSpace,
    handleSourceOrgs,
    handleSourceSpaces,
    handleDestOrgs,
    handleDestSpaces,
  } = useCloneDetails();

  const updateSelectedSourceOrgValue = useCallback(
    (value) => {
      setSelectedSourceOrgValue(value);
      setData((prev) => ({ ...prev, source_org_id: value?.value }));
    },
    [setData]
  );

  const updateSelectedDestOrgValue = useCallback(
    (value) => {
      setSelectedDestOrgValue(value);
      setData((prev) => ({ ...prev, destination_org_id: value?.value }));
    },
    [setData]
  );

  const updateSelectedSourceSpaceValue = useCallback(
    (value) => {
      setSelectedSourceSpaceValue(value);
      setData((prev) => ({ ...prev, source_space_id: value?.value }));
    },
    [setData]
  );

  const updateSelectedDestSpaceValue = useCallback(
    (value) => {
      setSelectedDestSpaceValue(value);
      setData((prev) => ({ ...prev, destination_space_id: value?.value }));
    },
    [setData]
  );

  useEffect(() => {
    setSelectedSourceOrgValue({});
    setSelectedDestOrgValue({});
    setSelectedSourceSpaceValue({});
    setSelectedDestSpaceValue({});
    setOrgInfo("");
  }, [open]);

  useEffect(() => {
    // Update dynamic text based on selected organizations
    setOrgInfo(() =>
      selectedSourceOrgValue &&
      selectedDestOrgValue &&
      selectedSourceSpaceValue &&
      selectedDestSpaceValue ? (
        <div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <p style={{ width: "400px" }}>
              <span style={{ fontWeight: "600" }}>Source Space : </span>{" "}
              {selectedSourceSpaceValue.label}
            </p>
            <p>
              <span style={{ fontWeight: "600" }}>Source Org : </span>
              {selectedSourceOrgValue.label}
            </p>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <p style={{ width: "400px" }}>
              <span style={{ fontWeight: "600" }}>Destination Space : </span>{" "}
              {selectedDestSpaceValue.label}
            </p>
            <p>
              <span style={{ fontWeight: "600" }}>Destination Org : </span>
              {selectedDestOrgValue.label}
            </p>
          </div>
        </div>
      ) : (
        ""
      )
    );
  }, [
    selectedSourceOrgValue,
    selectedDestOrgValue,
    selectedSourceSpaceValue,
    selectedDestSpaceValue,
  ]);

  useMemo(() => {
    const val =
      sourceOrgsData?.find((i) => i.value === selectedSourceOrg) ?? null;
    updateSelectedSourceOrgValue(val);
  }, [selectedSourceOrg, sourceOrgsData, updateSelectedSourceOrgValue]);

  useMemo(() => {
    const val = destOrgsData?.find((i) => i.value === selectedDestOrg) ?? null;
    updateSelectedDestOrgValue(val);
  }, [selectedDestOrg, destOrgsData, updateSelectedDestOrgValue]);

  useMemo(() => {
    const val =
      sourceSpacesData?.find((i) => i.value === selectedSourceSpace) ?? null;
    updateSelectedSourceSpaceValue(val);
  }, [selectedSourceSpace, sourceSpacesData, updateSelectedSourceSpaceValue]);

  useMemo(() => {
    const val =
      destSpacesData?.find((i) => i.value === selectedDestSpace) ?? null;
    updateSelectedDestSpaceValue(val);
  }, [selectedDestSpace, destSpacesData, updateSelectedDestSpaceValue]);

  const handleGoToNext = () => {
    const orgInfoData = { orgInfo };
    goToNext(orgInfoData);
  };

  return (
    <PdcLayout
      open={open}
      handleClose={handleClose}
      message="Select source organization, space and destination organization, space"
    >
      <div>
        <div className={classes.inputGroup}>
          <label htmlFor="sourceOrg" className={classes.label}>
            Source Organization
          </label>
          <PdcSelect
            required
            placeholder="Select Source Organization"
            className={classes.text_field}
            name="source_organization"
            options={sourceOrgsData}
            onChange={(val) => handleSourceOrgs({ target: val })}
            style={{ fontSize: "14px" }}
            isDisabled={!sourceOrgsData?.length > 0}
            isLoading={sourceOrganisations.processing}
            value={selectedSourceOrgValue}
          />
        </div>

        <div className={classes.inputGroup}>
          <label htmlFor="sourceWork" className={classes.label}>
            Source space
          </label>
          <PdcSelect
            required
            placeholder="Select Source Space"
            className={classes.text_field}
            name="workspace"
            options={sourceSpacesData}
            onChange={(val) => handleSourceSpaces({ target: val })}
            style={{ fontSize: "14px" }}
            isDisabled={!sourceSpacesData?.length > 0}
            isLoading={sourceSpaces.processing}
            value={selectedSourceSpaceValue}
          />
        </div>

        <div className={classes.inputGroup}>
          <label htmlFor="destOrg" className={classes.label}>
            Destination Organization
          </label>
          <PdcSelect
            required
            placeholder="Select Destination Organization"
            className={classes.text_field}
            name="destination_organization"
            options={destOrgsData}
            onChange={(val) => handleDestOrgs({ target: val })}
            style={{ fontSize: "14px" }}
            isDisabled={!destOrgsData?.length > 0}
            isLoading={destOrganisations.processing}
            value={selectedDestOrgValue}
          />
        </div>

        <div className={classes.inputGroup}>
          <label htmlFor="sourceOrg" className={classes.label}>
            Destination Space
          </label>
          <PdcSelect
            required
            placeholder="Select Destination Space"
            className={classes.text_field}
            name="workspace"
            options={destSpacesData}
            onChange={(val) => handleDestSpaces({ target: val })}
            style={{ fontSize: "14px" }}
            isDisabled={!destSpacesData?.length > 0}
            isLoading={destSpaces.processing}
            value={selectedDestSpaceValue}
          />
        </div>

        <div className={classes.btnGroup}>
          <button
            type="button"
            className={classes.btnCancel}
            onClick={handleClose}
          >
            Cancel
          </button>
          <Button
            variant="contained"
            color="primary"
            disableElevation={true}
            className={classes.btn}
            onClick={handleGoToNext}
          >
            View Apps
          </Button>
        </div>
      </div>
    </PdcLayout>
  );
};

export default AppToWorkModal;
